/**
 * API Service
 *
 * Provides typed API calls to the Hono backend.
 * Uses Clerk for authentication tokens.
 */

import type {
  TripSummaryWithPermission,
  TripScheduleData,
  LibraryData,
  POI,
  Tag,
  ChangelogEvent,
  CreateTripRequest,
  PermissionLevel,
  TripShare,
  UserProfile,
  InviteLink,
  InviteLinkInfo,
  AcceptInviteResult,
  Collection,
  CollectionWithPois,
  CollectionVisibility,
  CollectionInviteLink,
  CollectionInviteLinkInfo,
  AcceptCollectionInviteResult,
  PlacesSearchResponse,
  PlaceResult,
} from '@poi-planner/shared';

// API URL from environment
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Public config type (returned by /api/users/config)
export interface PublicConfig {
  mapboxApiKey: string | null;
}

// Fetch public config (no auth required)
export async function getPublicConfig(): Promise<PublicConfig> {
  const response = await fetch(`${API_URL}/api/users/config`);
  if (!response.ok) {
    throw new Error('Failed to fetch public config');
  }
  return response.json();
}

// Clerk instance (set by ClerkProvider)
let clerkInstance: any = null;

export function setClerkInstance(clerk: any) {
  clerkInstance = clerk;
}

// ── Fetch Helper ──

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchWithAuth<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (!clerkInstance) {
    throw new ApiError('NOT_INITIALIZED', 'Clerk not initialized', 500);
  }

  const token = await clerkInstance.session?.getToken();
  if (!token) {
    throw new ApiError('NOT_AUTHENTICATED', 'User not authenticated', 401);
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(
      data.code || 'API_ERROR',
      data.error || `Request failed with status ${response.status}`,
      response.status,
    );
  }

  return response.json();
}

// ── API Interface (for compatibility) ──

export interface ApiService {
  listTrips(): Promise<TripSummaryWithPermission[]>;
  createNewTrip(req: CreateTripRequest): Promise<TripScheduleData>;
  loadTripById(id: string): Promise<TripScheduleData | null>;
  saveTripById(id: string, data: TripScheduleData): Promise<void>;
  deleteTripById(id: string): Promise<void>;
  saveTripChangelog(tripId: string, events: ChangelogEvent[]): Promise<void>;
  getTripChangelog(tripId: string): Promise<ChangelogEvent[]>;
  loadLibrary(): Promise<LibraryData>;
  savePOIs(pois: POI[]): Promise<void>;
  saveLibraryChangelog(events: ChangelogEvent[]): Promise<void>;
  createTag(tag: Omit<Tag, 'id'>): Promise<Tag>;
  updateTag(id: string, updates: Partial<Omit<Tag, 'id'>>): Promise<Tag>;
  deleteTag(id: string): Promise<void>;
  loadTags(): Promise<Tag[]>;
  getTripPermission(tripId: string): Promise<PermissionLevel | 'owner' | null>;
  shareTrip(tripId: string, email: string, permission: PermissionLevel): Promise<TripShare>;
  updateSharePermission(shareId: string, permission: PermissionLevel): Promise<void>;
  removeShare(shareId: string): Promise<void>;
  getTripShares(tripId: string): Promise<TripShare[]>;
  getUserProfile(userId: string): Promise<UserProfile | null>;
  searchUsersByEmail(prefix: string): Promise<UserProfile[]>;
  createInviteLink(tripId: string, permission: PermissionLevel, label?: string): Promise<InviteLink>;
  getInviteLinks(tripId: string): Promise<InviteLink[]>;
  cancelInviteLink(linkId: string): Promise<void>;
  getInviteLinkInfo(token: string): Promise<InviteLinkInfo>;
  acceptInviteLink(token: string): Promise<AcceptInviteResult>;
  listCollections(): Promise<CollectionWithPois[]>;
  createCollection(name: string, description?: string, visibility?: CollectionVisibility): Promise<Collection>;
  updateCollection(id: string, updates: { name?: string; description?: string; visibility?: CollectionVisibility }): Promise<Collection>;
  deleteCollection(id: string): Promise<void>;
  getCollectionPois(collectionId: string): Promise<string[]>;
  addPoisToCollection(collectionId: string, poiIds: string[]): Promise<void>;
  removePoisFromCollection(collectionId: string, poiIds: string[]): Promise<void>;
  createCollectionInviteLink(collectionId: string, permission: PermissionLevel, label?: string): Promise<CollectionInviteLink>;
  getCollectionInviteLinks(collectionId: string): Promise<CollectionInviteLink[]>;
  cancelCollectionInviteLink(linkId: string): Promise<void>;
  getCollectionInviteLinkInfo(token: string): Promise<CollectionInviteLinkInfo>;
  acceptCollectionInviteLink(token: string): Promise<AcceptCollectionInviteResult>;
  getTripPoiIds(tripId: string): Promise<string[]>;
  getPinnedPoiIds(tripId: string): Promise<string[]>;
  pinPoisToTrip(tripId: string, poiIds: string[]): Promise<void>;
  unpinPoisFromTrip(tripId: string, poiIds: string[]): Promise<void>;
  searchPlaces(query: string): Promise<PlacesSearchResponse>;
  resolvePlaceUrl(url: string): Promise<{ result: PlaceResult | null; error?: string }>;
}

// ── API Implementation ──

export const api: ApiService = {
  // Multi-trip endpoints
  async listTrips() {
    const data = await fetchWithAuth<{ trips: TripSummaryWithPermission[] }>('/api/trips');
    return data.trips;
  },

  async createNewTrip(req) {
    return fetchWithAuth<TripScheduleData>('/api/trips', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  },

  async loadTripById(id) {
    try {
      // Server returns slightly different field names, need to transform
      const data = await fetchWithAuth<{
        trip: TripScheduleData['trip'];
        schedule: {
          days: TripScheduleData['schedule']['days'];
          transportEvents: Array<{
            id: string;
            dayId: string;
            mode: string;
            origin: { type: string; id: string };
            destination: { type: string; id: string };
            departureTime: string;
            durationMinutes: number;
            reference: string | null;
            notes: string | null;
            orderIndex: number;
          }>;
        };
        accommodationConfigs: Array<{
          poiId: string;
          color: number;
          checkInTime?: string;
          checkOutTime?: string;
        }>;
      }>(`/api/trips/${id}`);

      // Convert accommodationConfigs array to Record
      const configsRecord: TripScheduleData['schedule']['accommodationConfigs'] = {};
      for (const cfg of data.accommodationConfigs || []) {
        configsRecord[cfg.poiId] = {
          color: cfg.color,
          checkInTime: cfg.checkInTime,
          checkOutTime: cfg.checkOutTime,
        };
      }

      // Transform transportEvents field names to match shared types
      const transportEvents = (data.schedule.transportEvents || []).map((te) => ({
        id: te.id,
        dayId: te.dayId,
        mode: te.mode as any,
        origin: te.origin as any,
        destination: te.destination as any,
        departureTime: te.departureTime,
        duration: te.durationMinutes,
        reference: te.reference,
        notes: te.notes,
        order: te.orderIndex,
      }));

      return {
        trip: data.trip,
        schedule: {
          days: data.schedule.days,
          transportEvents,
          accommodationConfigs: configsRecord,
        },
      };
    } catch (err: any) {
      if (err.status === 404) return null;
      throw err;
    }
  },

  async saveTripById(id, data) {
    // Convert accommodationConfigs Record to array for server
    const configsArray = Object.entries(data.schedule.accommodationConfigs || {}).map(
      ([poiId, cfg]) => ({
        poiId,
        color: cfg.color,
        checkInTime: cfg.checkInTime,
        checkOutTime: cfg.checkOutTime,
      })
    );

    // Convert transportEvents field names back to server format
    const transportEvents = (data.schedule.transportEvents || []).map((te) => ({
      id: te.id,
      dayId: te.dayId,
      mode: te.mode,
      origin: te.origin,
      destination: te.destination,
      departureTime: te.departureTime,
      durationMinutes: te.duration,
      reference: te.reference,
      notes: te.notes,
      orderIndex: te.order,
    }));

    await fetchWithAuth(`/api/trips/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        schedule: {
          days: data.schedule.days,
          transportEvents,
        },
        accommodationConfigs: configsArray,
      }),
    });
  },

  async deleteTripById(id) {
    await fetchWithAuth(`/api/trips/${id}`, { method: 'DELETE' });
  },

  async saveTripChangelog(tripId, events) {
    await fetchWithAuth(`/api/trips/${tripId}/changelog`, {
      method: 'POST',
      body: JSON.stringify(events),
    });
  },

  async getTripChangelog(tripId) {
    const data = await fetchWithAuth<{ events: ChangelogEvent[] }>(`/api/trips/${tripId}/changelog`);
    return data.events;
  },

  // Library endpoints
  async loadLibrary() {
    return fetchWithAuth<LibraryData>('/api/library');
  },

  async savePOIs(pois) {
    await fetchWithAuth('/api/library/pois', {
      method: 'PUT',
      body: JSON.stringify({ pois }),
    });
  },

  async saveLibraryChangelog(events) {
    await fetchWithAuth('/api/library/changelog', {
      method: 'POST',
      body: JSON.stringify(events),
    });
  },

  // Tag endpoints
  async createTag(tag) {
    return fetchWithAuth<Tag>('/api/library/tags', {
      method: 'POST',
      body: JSON.stringify(tag),
    });
  },

  async updateTag(id, updates) {
    return fetchWithAuth<Tag>(`/api/library/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async deleteTag(id) {
    await fetchWithAuth(`/api/library/tags/${id}`, { method: 'DELETE' });
  },

  async loadTags() {
    const data = await fetchWithAuth<{ tags: Tag[] }>('/api/library/tags');
    return data.tags;
  },

  // Collaboration endpoints
  async getTripPermission(tripId) {
    try {
      const data = await fetchWithAuth<{ permission: PermissionLevel | 'owner' | null }>(`/api/trips/${tripId}/permission`);
      return data.permission;
    } catch {
      return null;
    }
  },

  async shareTrip(tripId, email, permission) {
    return fetchWithAuth<TripShare>(`/api/trips/${tripId}/shares`, {
      method: 'POST',
      body: JSON.stringify({ email, permission }),
    });
  },

  async updateSharePermission(shareId, permission) {
    await fetchWithAuth(`/api/trips/shares/${shareId}`, {
      method: 'PUT',
      body: JSON.stringify({ permission }),
    });
  },

  async removeShare(shareId) {
    await fetchWithAuth(`/api/trips/shares/${shareId}`, { method: 'DELETE' });
  },

  async getTripShares(tripId) {
    const data = await fetchWithAuth<{ shares: TripShare[] }>(`/api/trips/${tripId}/shares`);
    return data.shares;
  },

  async getUserProfile(userId) {
    try {
      return await fetchWithAuth<UserProfile>(`/api/users/${userId}`);
    } catch {
      return null;
    }
  },

  async searchUsersByEmail(prefix) {
    const data = await fetchWithAuth<{ users: UserProfile[] }>(`/api/users/search?q=${encodeURIComponent(prefix)}`);
    return data.users;
  },

  // Invite link endpoints
  async createInviteLink(tripId, permission, label) {
    return fetchWithAuth<InviteLink>(`/api/trips/${tripId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ permission, label }),
    });
  },

  async getInviteLinks(tripId) {
    const data = await fetchWithAuth<{ invites: InviteLink[] }>(`/api/trips/${tripId}/invites`);
    return data.invites;
  },

  async cancelInviteLink(linkId) {
    await fetchWithAuth(`/api/invites/${linkId}`, { method: 'DELETE' });
  },

  async getInviteLinkInfo(token) {
    return fetchWithAuth<InviteLinkInfo>(`/api/invites/${token}/info`);
  },

  async acceptInviteLink(token) {
    return fetchWithAuth<AcceptInviteResult>(`/api/invites/${token}/accept`, { method: 'POST' });
  },

  // Collection endpoints
  async listCollections() {
    const data = await fetchWithAuth<{ collections: CollectionWithPois[] }>('/api/collections');
    return data.collections;
  },

  async createCollection(name, description, visibility) {
    return fetchWithAuth<Collection>('/api/collections', {
      method: 'POST',
      body: JSON.stringify({ name, description, visibility }),
    });
  },

  async updateCollection(id, updates) {
    return fetchWithAuth<Collection>(`/api/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  async deleteCollection(id) {
    await fetchWithAuth(`/api/collections/${id}`, { method: 'DELETE' });
  },

  async getCollectionPois(collectionId) {
    const data = await fetchWithAuth<{ poiIds: string[] }>(`/api/collections/${collectionId}/pois`);
    return data.poiIds;
  },

  async addPoisToCollection(collectionId, poiIds) {
    await fetchWithAuth(`/api/collections/${collectionId}/pois`, {
      method: 'POST',
      body: JSON.stringify({ poiIds }),
    });
  },

  async removePoisFromCollection(collectionId, poiIds) {
    await fetchWithAuth(`/api/collections/${collectionId}/pois`, {
      method: 'DELETE',
      body: JSON.stringify({ poiIds }),
    });
  },

  async createCollectionInviteLink(collectionId, permission, label) {
    return fetchWithAuth<CollectionInviteLink>(`/api/collections/${collectionId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ permission, label }),
    });
  },

  async getCollectionInviteLinks(collectionId) {
    const data = await fetchWithAuth<{ invites: CollectionInviteLink[] }>(`/api/collections/${collectionId}/invites`);
    return data.invites;
  },

  async cancelCollectionInviteLink(linkId) {
    await fetchWithAuth(`/api/collection-invites/${linkId}`, { method: 'DELETE' });
  },

  async getCollectionInviteLinkInfo(token) {
    return fetchWithAuth<CollectionInviteLinkInfo>(`/api/collection-invites/${token}/info`);
  },

  async acceptCollectionInviteLink(token) {
    return fetchWithAuth<AcceptCollectionInviteResult>(`/api/collection-invites/${token}/accept`, { method: 'POST' });
  },

  // Trip POI IDs
  async getTripPoiIds(tripId) {
    const data = await fetchWithAuth<{ poiIds: string[] }>(`/api/trips/${tripId}/poi-ids`);
    return data.poiIds;
  },

  // Pinned POI endpoints
  async getPinnedPoiIds(tripId) {
    const data = await fetchWithAuth<{ poiIds: string[] }>(`/api/trips/${tripId}/pinned-pois`);
    return data.poiIds;
  },

  async pinPoisToTrip(tripId, poiIds) {
    await fetchWithAuth(`/api/trips/${tripId}/pinned-pois`, {
      method: 'POST',
      body: JSON.stringify({ poiIds }),
    });
  },

  async unpinPoisFromTrip(tripId, poiIds) {
    await fetchWithAuth(`/api/trips/${tripId}/pinned-pois`, {
      method: 'DELETE',
      body: JSON.stringify({ poiIds }),
    });
  },

  // Places search (proxied through server)
  async searchPlaces(query) {
    return fetchWithAuth<PlacesSearchResponse>('/api/places/search', {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  },

  async resolvePlaceUrl(url) {
    return fetchWithAuth<{ result: PlaceResult | null; error?: string }>('/api/places/resolve-url', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },
};
